import { useEffect, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { seededGradient } from '../utils';

const MEMBER_PAGE_SIZE = 100;

interface GuildDirectMessageDialogProps {
  /** The guild whose roster is offered; `null` keeps the dialog closed. */
  guild: { guildId: string; name: string } | null;
  currentUserId: string | null;
  onClose: () => void;
  onSelectMember: (guildId: string, userId: string) => Promise<void>;
}

/**
 * Picks a guild member to start a GUILD-SCOPED DM with — the conversation lands
 * inside that guild, separate from the personal DM with the same person.
 *
 * The roster is the picker's whole point: a guild DM is only valid between two
 * members, so offering the global user search here would just surface people the
 * server will reject.
 */
export function GuildDirectMessageDialog({
  guild,
  currentUserId,
  onClose,
  onSelectMember,
}: GuildDirectMessageDialogProps) {
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // `memberPicker`, not `members` — that key belongs to useGuildMembers'
  // useInfiniteQuery, and sharing one key across the two hook shapes corrupts
  // the cache (it crashes GuildDetailPage when both have run).
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: queryKeys.guilds.memberPicker(guild?.guildId ?? ''),
      queryFn: ({ pageParam = 0 }) =>
        api.getGuildMembers(guild!.guildId, { limit: MEMBER_PAGE_SIZE, offset: pageParam }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const nextOffset = allPages.length * MEMBER_PAGE_SIZE;
        return nextOffset < lastPage.total ? nextOffset : undefined;
      },
      enabled: !!guild,
    });

  // Clear transient state when switching guilds or reopening.
  useEffect(() => {
    setPendingUserId(null);
    setError(null);
  }, [guild?.guildId]);

  // Never offer to DM yourself here — "Saved Messages" is a personal channel and
  // has no guild-scoped counterpart.
  const members = (data?.pages.flatMap((page) => page.members) ?? []).filter(
    (m) => m.userId !== currentUserId,
  );

  const handleSelect = async (userId: string) => {
    if (!guild) return;
    setPendingUserId(userId);
    setError(null);
    try {
      await onSelectMember(guild.guildId, userId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open that conversation');
    } finally {
      setPendingUserId(null);
    }
  };

  return (
    <Dialog open={!!guild} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New message in {guild?.name}</DialogTitle>
          <DialogDescription>
            This conversation stays inside {guild?.name}, separate from your personal chat with the
            same person.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        {isLoading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">Loading members…</div>
        ) : isError ? (
          // A failed roster load is not "nobody here" — say so, and let the user
          // retry without closing and reopening the dialog.
          <div className="py-6 flex flex-col items-center gap-2">
            <p className="text-sm text-destructive">Couldn&apos;t load guild members.</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        ) : members.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            No other members in this guild yet.
          </div>
        ) : (
          <ul className="max-h-80 overflow-y-auto flex flex-col gap-1">
            {members.map((member) => {
              const label = member.username || member.userId;
              return (
                <li key={member.userId}>
                  <button
                    type="button"
                    disabled={pendingUserId !== null}
                    onClick={() => void handleSelect(member.userId)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors hover:bg-foreground/10 disabled:opacity-60 cursor-pointer"
                  >
                    <span
                      aria-hidden="true"
                      className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                      style={{ background: seededGradient(label) }}
                    >
                      {label.charAt(0).toUpperCase()}
                    </span>
                    <span className="flex-1 min-w-0 truncate text-sm">{label}</span>
                    <span className="flex-shrink-0 text-xs text-muted-foreground capitalize">
                      {pendingUserId === member.userId ? 'Opening…' : member.role}
                    </span>
                  </button>
                </li>
              );
            })}
            {hasNextPage && (
              <li>
                <button
                  type="button"
                  disabled={isFetchingNextPage}
                  onClick={() => void fetchNextPage()}
                  className="w-full px-3 py-2 rounded-lg text-center text-sm text-muted-foreground transition-colors hover:bg-foreground/10 disabled:opacity-60 cursor-pointer"
                >
                  {isFetchingNextPage ? 'Loading…' : 'Load more members'}
                </button>
              </li>
            )}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
